import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Move, ZoomIn, ZoomOut, RotateCcw, RotateCw, Save, X } from 'lucide-react';

const LogoPlacementEditor = ({ 
  mockupBase64, 
  logoBase64, 
  initialPosition,
  onSave, 
  onCancel,
  onPreview 
}) => {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [position, setPosition] = useState(initialPosition || { x: 50, y: 35, width: 35, height: 35, rotation: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [previewLoading, setPreviewLoading] = useState(false);

  // Update position when initialPosition changes
  useEffect(() => {
    if (initialPosition) {
      setPosition({ ...initialPosition, rotation: initialPosition.rotation || 0 });
    }
  }, [initialPosition]);

  const handleMouseDown = (e) => {
    if (e.target.classList.contains('resize-handle')) {
      setIsResizing(true);
    } else if (e.target.classList.contains('rotate-handle')) {
      // Don't start drag for rotate handle - handled separately
      return;
    } else {
      setIsDragging(true);
    }
    setDragStart({ x: e.clientX, y: e.clientY });
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging && !isResizing) return;
    if (!containerRef.current) return;

    const container = containerRef.current.getBoundingClientRect();
    const deltaX = ((e.clientX - dragStart.x) / container.width) * 100;
    const deltaY = ((e.clientY - dragStart.y) / container.height) * 100;

    if (isDragging) {
      setPosition(prev => ({
        ...prev,
        x: Math.max(5, Math.min(95, prev.x + deltaX)),
        y: Math.max(5, Math.min(95, prev.y + deltaY))
      }));
    } else if (isResizing) {
      setPosition(prev => ({
        ...prev,
        width: Math.max(10, Math.min(80, prev.width + deltaX)),
        height: Math.max(10, Math.min(80, prev.height + deltaY))
      }));
    }

    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  const adjustSize = (delta) => {
    setPosition(prev => ({
      ...prev,
      width: Math.max(10, Math.min(80, prev.width + delta)),
      height: Math.max(10, Math.min(80, prev.height + delta))
    }));
  };

  const adjustRotation = (delta) => {
    setPosition(prev => ({
      ...prev,
      rotation: ((prev.rotation || 0) + delta + 360) % 360
    }));
  };

  const resetPosition = () => {
    setPosition({ x: 50, y: 35, width: 35, height: 35, rotation: 0 });
  };

  const handlePreview = async () => {
    if (onPreview) {
      setPreviewLoading(true);
      try {
        await onPreview(position);
      } finally {
        setPreviewLoading(false);
      }
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(position);
    }
  };

  return (
    <div 
      style={{ 
        background: '#1a1a2e', 
        borderRadius: '12px', 
        padding: '20px',
        color: 'white'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
          🎯 Logo Placement Editor
        </h3>
        <Button variant="ghost" size="sm" onClick={onCancel} style={{ color: 'white' }}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <p style={{ color: '#a0aec0', fontSize: '14px', marginBottom: '16px' }}>
        Drag to move • Drag corners to resize • Use rotation buttons for angled placement
      </p>

      {/* Mockup Canvas */}
      <div 
        ref={containerRef}
        style={{ 
          position: 'relative',
          width: '100%',
          maxWidth: '500px',
          margin: '0 auto 20px',
          background: '#2d2d44',
          borderRadius: '8px',
          overflow: 'hidden',
          cursor: isDragging ? 'grabbing' : 'default'
        }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Mockup Image */}
        <img 
          src={`data:image/png;base64,${mockupBase64}`}
          alt="Mockup"
          style={{ width: '100%', display: 'block', pointerEvents: 'none' }}
          draggable={false}
        />

        {/* Draggable Logo Overlay */}
        {logoBase64 && (
          <div
            style={{
              position: 'absolute',
              left: `${position.x}%`,
              top: `${position.y}%`,
              width: `${position.width}%`,
              height: `${position.height}%`,
              transform: `translate(-50%, -50%) rotate(${position.rotation || 0}deg)`,
              cursor: isDragging ? 'grabbing' : 'grab',
              border: '2px dashed #667eea',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(102, 126, 234, 0.1)'
            }}
            onMouseDown={handleMouseDown}
          >
            <img
              src={`data:image/png;base64,${logoBase64}`}
              alt="Logo"
              style={{ 
                maxWidth: '100%', 
                maxHeight: '100%', 
                objectFit: 'contain',
                pointerEvents: 'none'
              }}
              draggable={false}
            />
            
            {/* Resize Handle */}
            <div
              className="resize-handle"
              style={{
                position: 'absolute',
                bottom: '-8px',
                right: '-8px',
                width: '16px',
                height: '16px',
                background: '#667eea',
                borderRadius: '50%',
                cursor: 'se-resize',
                border: '2px solid white'
              }}
              onMouseDown={handleMouseDown}
            />
            
            {/* Rotate Handle */}
            <div
              className="rotate-handle"
              style={{
                position: 'absolute',
                top: '-24px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '20px',
                height: '20px',
                background: '#48bb78',
                borderRadius: '50%',
                cursor: 'grab',
                border: '2px solid white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: 'white'
              }}
              title="Drag to rotate"
            >
              ↻
            </div>
          </div>
        )}

        {/* Position Indicator */}
        <div style={{
          position: 'absolute',
          bottom: '8px',
          left: '8px',
          background: 'rgba(0,0,0,0.7)',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '11px'
        }}>
          X: {position.x.toFixed(0)}% | Y: {position.y.toFixed(0)}% | Size: {position.width.toFixed(0)}% | Rot: {(position.rotation || 0).toFixed(0)}°
        </div>
      </div>

      {/* Size & Rotation Controls */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '16px' }}>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => adjustSize(-5)}
          style={{ background: '#2d2d44', borderColor: '#4a4a6a', color: 'white' }}
        >
          <ZoomOut className="w-4 h-4 mr-1" /> Smaller
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => adjustSize(5)}
          style={{ background: '#2d2d44', borderColor: '#4a4a6a', color: 'white' }}
        >
          <ZoomIn className="w-4 h-4 mr-1" /> Larger
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => adjustRotation(-15)}
          style={{ background: '#2d2d44', borderColor: '#48bb78', color: '#48bb78' }}
          title="Rotate 15° counter-clockwise"
        >
          <RotateCcw className="w-4 h-4 mr-1" /> -15°
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => adjustRotation(15)}
          style={{ background: '#2d2d44', borderColor: '#48bb78', color: '#48bb78' }}
          title="Rotate 15° clockwise"
        >
          <RotateCw className="w-4 h-4 mr-1" /> +15°
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={resetPosition}
          style={{ background: '#2d2d44', borderColor: '#4a4a6a', color: 'white' }}
        >
          Reset All
        </Button>
      </div>

      {/* Quick Position Presets */}
      <div style={{ marginBottom: '20px' }}>
        <p style={{ fontSize: '13px', color: '#a0aec0', marginBottom: '8px' }}>Quick Positions:</p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setPosition({ x: 50, y: 35, width: 35, height: 35, rotation: 0 })}
            style={{ background: '#2d2d44', borderColor: '#4a4a6a', color: 'white', fontSize: '12px' }}
          >
            Center Chest
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setPosition({ x: 30, y: 28, width: 15, height: 15, rotation: 0 })}
            style={{ background: '#2d2d44', borderColor: '#4a4a6a', color: 'white', fontSize: '12px' }}
          >
            Left Chest
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setPosition({ x: 70, y: 28, width: 15, height: 15, rotation: 0 })}
            style={{ background: '#2d2d44', borderColor: '#4a4a6a', color: 'white', fontSize: '12px' }}
          >
            Right Chest
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setPosition({ x: 50, y: 50, width: 50, height: 50, rotation: 0 })}
            style={{ background: '#2d2d44', borderColor: '#4a4a6a', color: 'white', fontSize: '12px' }}
          >
            Full Front
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setPosition({ x: 18, y: 40, width: 12, height: 12, rotation: -30 })}
            style={{ background: '#2d2d44', borderColor: '#48bb78', color: '#48bb78', fontSize: '12px' }}
          >
            Left Sleeve ↗
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setPosition({ x: 82, y: 40, width: 12, height: 12, rotation: 30 })}
            style={{ background: '#2d2d44', borderColor: '#48bb78', color: '#48bb78', fontSize: '12px' }}
          >
            Right Sleeve ↗
          </Button>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <Button variant="outline" onClick={onCancel} style={{ borderColor: '#4a4a6a', color: 'white' }}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          style={{ background: '#667eea', color: 'white' }}
          data-testid="save-logo-position-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          Apply Logo Position
        </Button>
      </div>
    </div>
  );
};

export default LogoPlacementEditor;
